import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_LOCATION,
  LOCATION_PREFERENCE,
  getCurrentLocationResult,
  getLastKnownLocation,
  getStoredLocationPreference,
  setLastKnownLocation,
  setStoredLocationPreference,
} from '../services/LocationService';
import { getApproximateLocation } from '../services/GeoService';
import { getCachedWeather, getWeather } from '../services/WeatherService';
import { findNearbyParks, getClosestPark } from '../services/ParkFinderService';
import { findNearbyPubs, getClosestPub } from '../services/PubFinderService';
import { isDayForIt } from '../utils/DayForItLogic';
import { isNearLocation } from '../utils/geo';
import { trackLocationPermissionResult } from '../utils/analytics';

const VENUE_FINDERS = {
  park: { find: findNearbyParks, closest: getClosestPark },
  pub: { find: findNearbyPubs, closest: getClosestPub },
};

const EMPTY_VENUE = { kind: null, closest: null, loading: false, failed: false };

export const venueKindFor = (status) => (status?.isDayForIt ? 'park' : 'pub');

/**
 * Cached weather (up to a few hours old) is shown instantly while fresh data loads, but only
 * when it belongs to the location we'll use. With location denied we always use Melbourne,
 * so a cache from anywhere else doesn't count.
 */
const getUsableCachedWeather = () => {
  const cached = getCachedWeather({ allowStale: true });
  if (!cached?.data?.current) return null;

  if (getStoredLocationPreference() === LOCATION_PREFERENCE.denied) {
    return isNearLocation(cached.location, DEFAULT_LOCATION) ? cached : null;
  }

  return cached;
};

/**
 * A location to show a verdict for *while* the browser's location request is still running:
 * - previously allowed: where they were last time
 * - first visit: their approximate area from our own /api/geo, else Melbourne
 */
const getProvisionalLocation = async (savedPreference) => {
  if (savedPreference === LOCATION_PREFERENCE.allowed) {
    return getLastKnownLocation() ?? getCachedWeather({ allowStale: true })?.location ?? null;
  }

  return (await getApproximateLocation()) ?? DEFAULT_LOCATION;
};

/**
 * Where to settle once the browser has answered: its fix if it gave one, Melbourne if the user
 * said no, otherwise (prompt ignored, no fix) keep the provisional location.
 */
const getFinalLocation = (geo, provisionalLocation) => {
  if (geo.source === 'browser') return { location: geo.location, source: 'browser' };
  if (geo.source === 'denied' || !provisionalLocation) return { location: DEFAULT_LOCATION, source: 'default' };
  return { location: provisionalLocation.location, source: provisionalLocation.source };
};

/** Stored preference after a browser answer: only a real yes or no changes it. */
const preferenceAfter = (geoSource, savedPreference) => {
  if (geoSource === 'browser') return LOCATION_PREFERENCE.allowed;
  if (geoSource === 'denied') return LOCATION_PREFERENCE.denied;
  return savedPreference;
};

/**
 * Location → weather → verdict → nearest venue.
 *
 * Built to show *a* verdict as fast as possible, then improve it:
 * - A matching cached verdict (up to 3 hours old) shows straight after mount.
 * - Weather is fetched for a provisional location (last known, or approximate on a first
 *   visit) while the browser's location request runs, so nobody waits on the permission prompt.
 * - When the precise location arrives, it's only refetched if it's actually somewhere else.
 * - Respects the stored location preference: a "denied" user is never prompted on load.
 * - Newer requests supersede older ones, so stale responses never overwrite fresh state.
 *
 * @param {{ includeVenues?: boolean }} options - set false for pages that only need the verdict
 */
export function useDayForIt({ includeVenues = true } = {}) {
  // Initial state must match the prerendered HTML, which has no localStorage: client-only
  // values (cached weather, stored preference) are applied right after mount instead.
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationSource, setLocationSource] = useState(null); // 'browser' | 'last-known' | 'approximate' | 'default'
  const [venue, setVenue] = useState(EMPTY_VENUE);
  const [locationPreference, setLocationPreference] = useState(null);
  const [requestingLocation, setRequestingLocation] = useState(false);

  // Request counters: a newer request supersedes older ones, whose results are then ignored.
  const requestsRef = useRef({ run: 0, verdict: 0, venue: 0 });

  const lookupVenue = useCallback(async (kind, nextLocation) => {
    const requests = requestsRef.current;
    const venueRunId = ++requests.venue;
    const isCurrent = () => requests.venue === venueRunId;
    const finder = VENUE_FINDERS[kind];

    setVenue({ kind, closest: null, loading: true, failed: false });

    try {
      const venues = await finder.find(nextLocation.latitude, nextLocation.longitude);
      if (isCurrent()) {
        setVenue({ kind, closest: finder.closest(venues), loading: false, failed: false });
      }
    } catch (err) {
      console.error(`Error finding ${kind}s:`, err);
      if (isCurrent()) {
        setVenue({ kind, closest: null, loading: false, failed: true });
      }
    }
  }, []);

  /**
   * @param {object} options
   * @param {boolean} options.promptForLocation - ask the browser even if the user previously denied
   * @param {string} options.entryPoint - analytics label for the location permission result
   * @param {string} options.errorMessage - shown if nothing could be loaded
   * @param {boolean} options.keepDataOnError - a verdict is already showing; keep it on failure
   */
  const refresh = useCallback(async ({ promptForLocation, entryPoint, errorMessage, keepDataOnError }) => {
    const requests = requestsRef.current;
    const runId = ++requests.run;
    const isCurrent = () => requests.run === runId;
    let hasVerdict = keepDataOnError;
    let shown = null; // { location, source } of the latest verdict requested in this run

    // Fetch and show the verdict for a location. A later call supersedes an earlier one.
    // Resolves true when shown, false when superseded; rejects if the weather request fails.
    const showVerdictFor = async (nextLocation, source) => {
      const verdictId = ++requests.verdict;
      const isLatest = () => isCurrent() && requests.verdict === verdictId;
      shown = { location: nextLocation, source };

      setLocation(nextLocation);
      setLocationSource(source);
      requests.venue++; // Drop any venue lookup for the previous location
      setVenue(EMPTY_VENUE);

      const weatherData = await getWeather(nextLocation.latitude, nextLocation.longitude);
      if (!isLatest()) return false;

      const nextStatus = isDayForIt(weatherData.current);
      setWeather(weatherData.current);
      setStatus(nextStatus);
      setError(null);
      setLoading(false);
      hasVerdict = true;

      if (includeVenues) {
        lookupVenue(venueKindFor(nextStatus), nextLocation);
      }
      return true;
    };

    try {
      const savedPreference = getStoredLocationPreference();

      if (!promptForLocation && savedPreference === LOCATION_PREFERENCE.denied) {
        await showVerdictFor(DEFAULT_LOCATION, 'default');
        return;
      }

      let geo = null;
      const geoRequest = getCurrentLocationResult().then((result) => { geo = result; return result; });

      // Don't wait on the browser (or its permission prompt): show a provisional verdict now.
      let provisional = null;
      let provisionalShown = Promise.resolve(false);
      if (!promptForLocation) {
        const provisionalRequest = getProvisionalLocation(savedPreference);
        let provisionalLocation = await Promise.race([
          provisionalRequest,
          geoRequest.then(() => null), // the browser answered first
        ]);
        // The browser answered first but without a fix or a "no" (unavailable, unsupported):
        // the provisional location is still the best we have.
        if (!provisionalLocation && geo && geo.source !== 'browser' && geo.source !== 'denied') {
          provisionalLocation = await provisionalRequest;
        }
        if (!isCurrent()) return;

        const browserGaveAnswer = geo && (geo.source === 'browser' || geo.source === 'denied');
        if (provisionalLocation && !browserGaveAnswer) {
          provisional = {
            location: provisionalLocation,
            source: savedPreference === LOCATION_PREFERENCE.allowed ? 'last-known' : 'approximate',
          };
          provisionalShown = showVerdictFor(provisional.location, provisional.source).catch((err) => {
            console.error(err);
            return false;
          });
        }
      }

      await geoRequest;
      if (!isCurrent()) return;

      const nextPreference = preferenceAfter(geo.source, savedPreference);
      if (nextPreference) {
        setStoredLocationPreference(nextPreference);
      }
      setLocationPreference(nextPreference);
      if (geo.source === 'browser') {
        setLastKnownLocation(geo.location);
      }
      trackLocationPermissionResult({
        source: geo.source,
        entryPoint: entryPoint ?? (savedPreference === null ? 'first_visit' : 'stored_preference'),
      });

      const final = getFinalLocation(geo, provisional);
      const alreadyShowingFinal = shown && isNearLocation(shown.location, final.location);

      if (alreadyShowingFinal && await provisionalShown) {
        if (isCurrent()) setLocationSource(final.source);
        return;
      }
      if (!isCurrent()) return;

      await showVerdictFor(final.location, final.source);
    } catch (err) {
      console.error(err);
      if (!isCurrent()) return;

      if (hasVerdict) {
        // Keep the verdict on screen, but don't leave the venue section waiting forever.
        setVenue((current) => (current.loading || !current.kind ? { ...EMPTY_VENUE, failed: true } : current));
      } else {
        setError(errorMessage);
      }
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  }, [includeVenues, lookupVenue]);

  useEffect(() => {
    const requests = requestsRef.current;

    /* eslint-disable react-hooks/set-state-in-effect --
       Two-pass rendering for client-only data: localStorage doesn't exist when the page is
       prerendered, so it's applied after hydration rather than in the initial state. refresh()
       itself only sets state after awaiting location/weather (the standard fetch-in-effect pattern). */
    const cached = getUsableCachedWeather();
    setLocationPreference(getStoredLocationPreference());
    if (cached) {
      setWeather(cached.data.current);
      setStatus(isDayForIt(cached.data.current));
      setLocation(cached.location ?? null);
      setLoading(false);
    }

    refresh({
      promptForLocation: false,
      errorMessage: 'Failed to load weather data.',
      keepDataOnError: Boolean(cached),
    });
    /* eslint-enable react-hooks/set-state-in-effect */

    return () => {
      // Invalidate in-flight work on unmount (and on StrictMode's dev re-mount).
      requests.run++;
      requests.verdict++;
      requests.venue++;
    };
  }, [refresh]);

  const enableLocation = useCallback(async () => {
    setError(null);
    setRequestingLocation(true);

    try {
      await refresh({
        promptForLocation: true,
        entryPoint: 'retry_button',
        errorMessage: 'Failed to update your location.',
        keepDataOnError: false,
      });
    } finally {
      setRequestingLocation(false);
    }
  }, [refresh]);

  /**
   * Development helper: override the verdict (true/false), or pass null to restore the real one.
   * Fetches the matching venue if we don't already have it.
   */
  const forceVerdict = useCallback((forcedIsDayForIt) => {
    const nextStatus = forcedIsDayForIt === null
      ? (weather ? isDayForIt(weather) : null)
      : {
        isDayForIt: forcedIsDayForIt,
        reason: forcedIsDayForIt
          ? 'Forced: Perfect weather for the park!'
          : "Forced: Nah, let's go to the pub.",
      };

    if (!nextStatus) return;

    setStatus(nextStatus);

    const kind = venueKindFor(nextStatus);
    if (includeVenues && location && venue.kind !== kind) {
      lookupVenue(kind, location);
    }
  }, [weather, includeVenues, location, venue.kind, lookupVenue]);

  return {
    status,
    weather,
    loading,
    error,
    location,
    locationSource,
    venue,
    locationPreference,
    requestingLocation,
    enableLocation,
    forceVerdict,
  };
}
